import React, { useEffect, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const SourceLineChart = ({ data, height }) => {
  const containerRef = useRef(null);
  const [themeColors, setThemeColors] = useState({
    axisColor: "#8B9599", // fallback
     axisXColor: "#8B9599", // fallback
      axisborderColor: "#8B9599", // fallback
      axisgridColor: "#8B9599", // fallback
    successLabel: "#5DC2B7", // fallback
    failLabel: "#FF8780", // fallback
  });

  // Helper to get CSS variable with fallback
  const getCSSVar = (el, name, fallback) => {
    if (!el) return fallback;
    const val = getComputedStyle(el).getPropertyValue(name);
    return val ? val.trim() : fallback;
  };

  useEffect(() => {
    if (containerRef.current) {
      const style = getComputedStyle(containerRef.current);

      setThemeColors({
        axisColor: getCSSVar(containerRef.current, "--chartline-axis-color", "#8B9599"),
        axisXColor: getCSSVar(containerRef.current, "--chartline-axisX-color", "#8B9599"),
         axisgridColor: getCSSVar(containerRef.current, "--chartline-axisgrid-color", "#8B9599"),
           axisborderColor: getCSSVar(containerRef.current, "--chartline-axisborder-color", "#8B9599"),
        successLabel: getCSSVar(containerRef.current, "--chartline-success-label", "#5DC2B7"),
        failLabel: getCSSVar(containerRef.current, "--chartline-fail-label", "#FF8780"),
      });
    }
  }, []);

  if (!data || data.length === 0) {
    return (
      <div
        ref={containerRef}
        className="text-center py-4"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: height || "100%",
          flex: 1,
        }}
      >
        <img
          src="assets/images/emptystate.gif"
          style={{ width: "180px", opacity: 0.7, marginTop: "-40px" }}
          alt="No Data Available"
        />
        <p className="text-muted mt-2">No scan data available</p>
      </div>
    );
  }

  // Format dates
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  // Prepare chart data (unchanged)
  const chartData = {
    labels: data.map((item) => formatDate(item.date)),
    datasets: [
      {
        label: "Success Scans",
        data: data.map((item) => item.success_count),
        borderColor: "#5DC2B7",
        backgroundColor: "#5DC2B7",
        tension: 0.3,
        fill: false,
      },
      {
        label: "Failed Scans",
        data: data.map((item) => item.failed_count),
        borderColor: "#FF8780",
        backgroundColor: "#FF8780",
        tension: 0.3,
        fill: false,
      },
    ],
  };

  // Chart options (only colors theme-driven)
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          boxWidth: 12,
          padding: 20,
          usePointStyle: true,
          pointStyle: "rect",
          color: (ctx) => {
            if (ctx.text === "Success Scans") return themeColors.successLabel;
            if (ctx.text === "Failed Scans") return themeColors.failLabel;
            return themeColors.axisColor; // fallback
          },
        },
      },
      tooltip: {
        mode: "index",
        intersect: false,
        callbacks: {
          title: (context) => {
            const date = new Date(data[context[0].dataIndex].date);
            return date.toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            });
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          color: themeColors.axisXColor,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
          color: themeColors.axisXColor,
        },
        title: {
          display: true,
          text: "Number of Scans",
          color: themeColors.axisXColor,
        },
         grid: {
    color: themeColors.axisgridColor,   // <--- add this
  },
  border: {
    color: themeColors.axisborderColor,   // <--- add this
  }
      },
    },
    interaction: {
      mode: "nearest",
      axis: "x",
      intersect: false,
    },
  };

  return (
    <div
      ref={containerRef}
      style={{
        height: height || "100%",
        width: "100%",
      }}
    >
      <Line
        data={chartData}
        options={options}
        style={{
          height: height || "100%",
          width: "100%",
        }}
      />
    </div>
  );
};

export default SourceLineChart;
